import { type LucideIcon, Sparkles, Zap } from 'lucide-react';

import { Label } from '@apps/web/components/ui/label';
import { cn } from '@apps/web/lib/utils';

export type OutreachUrgency = 'normal' | 'urgent';

const URGENCY_OPTIONS: ReadonlyArray<{
    readonly value: OutreachUrgency;
    readonly label: string;
    readonly description: string;
    readonly icon: LucideIcon;
}> = [
    {
        value: 'normal',
        label: 'Normal',
        description:
            'Phoebe works to reach the best-fit caregivers. Great for open shifts several days out and new clients',
        icon: Sparkles,
    },
    {
        value: 'urgent',
        label: 'Urgent',
        description:
            'Phoebe reaches out to many caregivers for high-urgency shifts. Use for time-sensitive fills.',
        icon: Zap,
    },
];

type OutreachUrgencySelectorProps = {
    readonly value: OutreachUrgency;
    readonly onValueChange: (value: OutreachUrgency) => void;
    readonly disabled?: boolean;
};

export function OutreachUrgencySelector({
    value,
    onValueChange,
    disabled = false,
}: OutreachUrgencySelectorProps) {
    return (
        <div className="space-y-2">
            <Label>Urgency</Label>
            <div
                role="radiogroup"
                aria-label="Urgency"
                className="grid grid-cols-1 gap-2 sm:grid-cols-2"
            >
                {URGENCY_OPTIONS.map((option) => {
                    const selected = value === option.value;
                    const isUrgentOption = option.value === 'urgent';
                    const Icon = option.icon;
                    return (
                        <button
                            key={option.value}
                            type="button"
                            role="radio"
                            aria-checked={selected}
                            disabled={disabled}
                            onClick={() => onValueChange(option.value)}
                            className={cn(
                                'focus-visible:ring-ring flex cursor-pointer flex-col gap-1 rounded-lg border p-3 text-left transition-colors focus-visible:ring-2 focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-60',
                                selected
                                    ? isUrgentOption
                                        ? 'border-red-500 bg-red-50'
                                        : 'border-primary bg-muted/40'
                                    : 'border-input hover:bg-muted/50',
                            )}
                        >
                            <span className="flex items-center gap-2 text-sm font-medium">
                                <Icon
                                    aria-hidden={true}
                                    className={cn(
                                        'h-4 w-4',
                                        isUrgentOption
                                            ? 'text-red-500'
                                            : 'text-violet-500',
                                    )}
                                />
                                {option.label}
                            </span>
                            <span className="text-muted-foreground text-xs">
                                {option.description}
                            </span>
                        </button>
                    );
                })}
            </div>
        </div>
    );
}
